import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import type { BoxProps } from '../Box/Box';
import Box from '../Box/Box';
import type { ViewProps } from '../View/View';
import View from '../View/View';
import Input from '../Input';
import Text from '../Text';
import type { InputProps } from '../Input/Input';
import type { DatePickerProps } from '../DatePicker/DatePicker';
import DatePicker from '../DatePicker/DatePicker';
import type { Props as ReactSelectProps } from 'react-select';
import ReactSelect from 'react-select';
import { reactSelectStyles } from '../../../lib/form-util';
import type {
  LocationSelectProps,
  LocationSelectValue,
} from '../../LocationSelect';
import LocationSelect from '../../LocationSelect';
import type { DateRangePickerProps } from '../DateRangePicker';
import DateRangePicker from '../DateRangePicker';
import styled from 'styled-components';
import useEvent from '../../hooks/useEvent';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { uniq } from 'lodash';
import { transformRecord } from '../../../lib/util';

type FilterBarProps = {
  children?: React.ReactNode;
};

const FilterBar = ({
  children,
  ...restProps
}: FilterBarProps & BoxProps): JSX.Element => {
  return (
    <Box
      display="flex"
      height={48}
      bg="filterbar.background"
      flex="0 0 auto"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      overflowX="auto"
      px="3"
      {...restProps}
    >
      {children}
    </Box>
  );
};

type FilterBarTabProps = {
  onPress: () => void;
  children?: React.ReactNode;
  isActive: boolean;
} & ViewProps;

const FilterBarTabBg = styled(View)`
  cursor: pointer;
`;

export const FilterBarTab = ({
  children,
  isActive,
  onPress,
  ...rest
}: FilterBarTabProps): JSX.Element => {
  return (
    <FilterBarTabBg
      role="button"
      onClick={onPress}
      height={48}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      px={5.99}
      borderBottomStyle="solid"
      borderBottomWidth={5}
      borderBottomColor={isActive ? 'yellow.yellow_300' : 'transparent'}
      borderTopStyle="solid"
      borderTopWidth={4}
      borderTopColor="transparent"
      {...rest}
    >
      <Text color="neutral.white_50" fontWeight="bold">
        {children}
      </Text>
    </FilterBarTabBg>
  );
};

interface FilterBarInputBaseProps extends ViewProps {
  children: React.ReactNode;
  label: string;
  labelMinWidth?: number;
}

const FilterBarInputBase = ({
  children,
  label,
  flexShrink,
  labelMinWidth,
  ...rest
}: FilterBarInputBaseProps) => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="stretch"
      overflow="hidden"
      flexShrink={flexShrink ?? 0}
      {...rest}
    >
      <Box
        display="flex"
        borderTopLeftRadius={4}
        borderBottomLeftRadius={4}
        bg="neutral.gray_50"
        alignItems="center"
        px={3}
        flexShrink={flexShrink ?? 0}
        minWidth={labelMinWidth}
      >
        <Text
          color="neutral.black_900"
          fontWeight="bold"
          fontSize="1"
          isUppercase
        >
          {label}
        </Text>
      </Box>
      {children}
    </Box>
  );
};

interface FilterBarInputProps
  extends Omit<FilterBarInputBaseProps, 'children'> {
  name: string;
  inputProps?: InputProps;
}

export const FilterBarInput = forwardRef(
  (
    { name, inputProps, ...baseProps }: FilterBarInputProps,
    ref: React.Ref<HTMLInputElement>,
  ): JSX.Element => {
    return (
      <FilterBarInputBase {...baseProps}>
        <Input
          ref={ref}
          borderTopRightRadius={4}
          borderBottomRightRadius={4}
          autoComplete={'off'}
          border="none"
          width={100}
          name={name}
          {...inputProps}
        />
      </FilterBarInputBase>
    );
  },
);

interface FilterBarCompletionsInputProps
  extends Omit<FilterBarInputBaseProps, 'children'> {
  name: string;
  inputProps?: InputProps & {
    onChange?: undefined;
    value?: string;
  };
  completions?: Record<string, string[]>;
  onChange?: (newValue: string) => void;
}

export type QueryExpression<Facet> = [
  facet: Facet | null,
  value: string,
  exactMatch: boolean,
];
export type AndQuery<Facet> = QueryExpression<Facet>[];
export type OrQuery<Facet> = AndQuery<Facet>[];
export type FacetedFilterQuery<Facet extends string = string> = OrQuery<Facet>;

/** Parses a faceted filter query string into a list of facet:value pairs
 * Example: parseFacetedFilterQuery('inquiry loadingPort:vaasa destinationPort:dam') =>
 *  [[null, 'inquiry'], ['loadingPort', 'vaasa'], ['destinationPort', 'dam']]
 * Using quotes around a value will match the exact value, e.g. loadingUnit:"cbm" will NOT match scbm */
export const parseFacetedFilterQuery = (
  query: string,
  knownFacets: string[] = [],
): FacetedFilterQuery | null => {
  const trimmed = query.replace(/\s+and\s/gi, ' ').trim();
  if (trimmed === '') {
    return null;
  }
  const ors = trimmed.split(/\s+or\s/gi);
  return ors.map((query) =>
    query
      .split(/\s+/g)
      .map((part): [facet: string | null, value: string] => {
        // If no colon, return as-is
        const colonIndex = part.indexOf(':');
        if (colonIndex === -1) {
          return [null, part];
        }

        // Check if it's a legitimate facet
        const maybeFacet = part.substring(0, colonIndex);
        if (!maybeFacet || maybeFacet === '') {
          return [null, part];
        }

        // If facet isn't recognized and knownFacets is provided, treat whole string as value
        if (knownFacets.length > 0 && !knownFacets.includes(maybeFacet)) {
          return [null, part];
        }

        // Process as normal facet:value pair
        const value = part.substring(colonIndex + 1);
        return [maybeFacet, value];
      })
      .map(([facet, value]) => [
        facet,
        value?.replaceAll('"', ''),
        (value?.startsWith('"') && value?.endsWith('"')) ?? false,
      ]),
  );
};

/** Example:
 * filter = [['loadingPort', 'vaasa'], [null, 'DAM']]
 * item = { loadingPort: ['vaasa', 'helsinki'], destinationPort: ['rotterdam'] }
 * doesFacetedFilterMatch(filter, item) => true
 * -- null matches against any facet, and DAM matches regardless of case and being only part of the word */
export const doesFacetedFilterMatch = <Facet extends string = string>(
  filter: FacetedFilterQuery<Facet>,
  item: Record<Facet, string[]>,
) => {
  // Every expression in filter must match at least one item
  return filter.some((andQuery) =>
    andQuery.every(([facet, value, isExactMatch]) => {
      return Object.entries(item).some(
        ([itemFacet, itemValues]: [Facet, string[]]) => {
          return (
            (facet === null || facet === itemFacet) &&
            itemValues.some((itemValue) =>
              isExactMatch
                ? itemValue.toLowerCase() === value.toLowerCase()
                : itemValue.toLowerCase().includes(value.toLowerCase()),
            )
          );
        },
      );
    }),
  );
};

/** Get and combine completions from all groups */
export const getCompletionsFromAllMatchers = (
  matchersByGroup: Map<unknown, Record<string, string[]>>,
): Record<string, string[]> => {
  // Merge all matchers into a single collection
  const allMatchersSets: Record<string, Set<string>> = {};

  for (const [, matchers] of matchersByGroup) {
    for (const [key, values] of Object.entries(matchers)) {
      const set = (allMatchersSets[key] ??= new Set());
      for (const value of values) {
        set.add(value);
      }
    }
  }
  return transformRecord(allMatchersSets, (set) => Array.from(set));
};

type MatchedExpression = {
  /** The full expression between whitespace, may include a ":" */
  full: string;
  /** The current single word, doesn't include a ":", equals either the facet or value */
  word: string;
  /** The left side of ":" */
  facet: string | null;
  /** The right side of ":", or the full expression if there is no ":" */
  value: string;
  fullBounds: [number, number];
  wordBounds: [number, number];
};

/** Returns the facet:value information at the given index in the string. Index must be within bounds.
 * Examples:
 *   getExpressionAtIndex('loadingPort:vaasa baz', 4) => { full: 'loadingPort:vaasa', word: 'loadingPort', facet: 'loadingPort', value: 'vaasa', fullBounds: [0, 17], wordBounds: [0, 11] }
 *   getExpressionAtIndex('loadingPort:vaasa baz', 15) => { full: 'loadingPort:vaasa', word: 'vaasa', facet: loadingPort, value: 'vaasa', fullBounds: [0, 17], wordBounds: [12, 17] }
 *   getExpressionAtIndex('   river', 5) => { full: 'river', word: 'river', facet: null, value: 'river', fullBounds: [3, 8], wordBounds: [3, 8] }
 */
function getExpressionAtIndex(s: string, index: number): MatchedExpression {
  // Find the start of the word
  let wordStart = index;
  while (
    wordStart > 0 &&
    s[wordStart - 1] !== ' ' &&
    s[wordStart - 1] !== ':'
  ) {
    wordStart--;
  }

  // Find the end of the word
  let wordEnd = index;
  while (wordEnd < s.length && s[wordEnd] !== ' ' && s[wordEnd] !== ':') {
    wordEnd++;
  }

  let fullStart = wordStart;
  while (fullStart > 0 && s[fullStart - 1] !== ' ') {
    fullStart--;
  }

  let fullEnd = wordEnd;
  while (fullEnd < s.length && s[fullEnd] !== ' ') {
    fullEnd++;
  }

  const full = s.slice(fullStart, fullEnd);
  const split = full.split(':');
  const facet = split.length > 1 ? split[0] : null;
  const value = split.length > 1 ? split[1] : split[0];
  const word = s.slice(wordStart, wordEnd);
  return {
    full,
    word,
    facet,
    value,
    fullBounds: [fullStart, fullEnd],
    wordBounds: [wordStart, wordEnd],
  };
}

export const FilterBarCompletionsInput = forwardRef(
  (
    {
      completions,
      name,
      onChange,
      inputProps,
      ...baseProps
    }: FilterBarCompletionsInputProps,
    ref: React.MutableRefObject<HTMLInputElement | null>,
  ): JSX.Element => {
    const [localInputValue, setLocalInputValue] = useState('');
    const [matchingSuggestions, setMatchingSuggestions] = useState<string[]>(
      [],
    );
    const [selectedIndex, setSelectedIndex] = useState<null | number>(null);
    const MAX_SUGGESTIONS = 6;

    /** Top level matches */
    const completionsKeys = useMemo(
      () => Object.keys(completions ?? {}),
      [completions],
    );

    /** All matchable words */
    const allCompletionValues = useMemo(
      () =>
        uniq([...completionsKeys, ...Object.values(completions ?? {}).flat()]),
      [completions, completionsKeys],
    );

    const inputValue = inputProps?.value ?? localInputValue;
    const setInputValue = onChange ? onChange : setLocalInputValue;

    const [currentExpression, setCurrentExpression] =
      useState<MatchedExpression>(
        getExpressionAtIndex(inputValue, inputValue.length),
      );

    const selectSuggestion = useEvent((selectedSuggestion: string) => {
      if (!currentExpression) return;
      const { full, word, wordBounds } = currentExpression;

      // Insert the selected suggestion into the string at wordBounds
      const insertValue =
        full == word && completions?.[selectedSuggestion]
          ? `${selectedSuggestion}:`
          : selectedSuggestion;
      const newValue =
        inputValue.slice(0, wordBounds[0]) +
        insertValue +
        inputValue.slice(wordBounds[1]);
      setInputValue(newValue);
      setMatchingSuggestions([]);
      onChange?.(newValue);
    });

    useEffect(() => {
      if (matchingSuggestions.length === 0) {
        setSelectedIndex(null);
      }
    }, [matchingSuggestions]);

    // Update current expression when the input value changes
    useEffect(() => {
      if (!completions) return;

      const expression = getExpressionAtIndex(
        inputValue,
        ref.current?.selectionStart ?? 0,
      );
      setCurrentExpression(expression);
    }, [completions, inputValue, ref]);

    // Update available suggestions when the current expression changes
    useEffect(() => {
      if (!currentExpression || !completions) return;

      const { facet, word } = currentExpression;
      const suggestions =
        word === facet || !facet
          ? word === ''
            ? completionsKeys
            : allCompletionValues
          : (completions[facet] ?? []);
      const wordLower = word.toLowerCase();
      const matches = suggestions
        .filter((s) => s.toLowerCase().includes(wordLower))
        .slice(0, MAX_SUGGESTIONS);

      setMatchingSuggestions(matches);
    }, [allCompletionValues, completions, completionsKeys, currentExpression]);

    // Suggestions navigation and selection
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (matchingSuggestions.length === 0) return;

      if (e.code === 'Escape') {
        setSelectedIndex(null);
        e.preventDefault();
        e.stopPropagation();
      } else if (e.code === 'ArrowDown') {
        setSelectedIndex((prevIndex) =>
          Math.min(matchingSuggestions.length - 1, (prevIndex ?? -1) + 1),
        );
        e.preventDefault();
      } else if (e.code === 'ArrowUp') {
        setSelectedIndex((prevIndex) =>
          prevIndex === null || prevIndex === 0 ? null : prevIndex - 1,
        );
        e.preventDefault();
      } else if (
        selectedIndex !== null &&
        (e.code === 'Enter' || e.code === 'Tab')
      ) {
        selectSuggestion(matchingSuggestions[selectedIndex]);
        e.preventDefault();
      } else {
        setSelectedIndex(matchingSuggestions.length > 0 ? 0 : null);
      }
    };

    const [referenceElement, setReferenceElement] =
      React.useState<HTMLInputElement | null>(null);
    const [popperElement, setPopperElement] =
      React.useState<HTMLUListElement | null>(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement);

    return (
      <FilterBarInputBase {...baseProps}>
        <Input
          ref={(refValue) => {
            ref.current = refValue;
            setReferenceElement(refValue);
          }}
          borderTopRightRadius={4}
          borderBottomRightRadius={4}
          autoComplete={'off'}
          border="none"
          width={100}
          name={name}
          value={inputValue}
          onKeyDown={handleKeyDown}
          {...inputProps}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => matchingSuggestions.length > 0 && setSelectedIndex(0)}
          onBlur={() => setSelectedIndex(null)}
        />
        {matchingSuggestions.length > 0 &&
          selectedIndex !== null &&
          createPortal(
            <ul
              ref={setPopperElement}
              {...attributes.popper}
              style={{
                ...styles.popper,
                listStyleType: 'none',
                top: '-14px',
                left: '-60px',
                padding: '2px',
                background: '#eee',
                zIndex: 100000,
              }}
            >
              {matchingSuggestions.map((suggestion, index) => (
                <li
                  key={suggestion}
                  style={{
                    background:
                      index === selectedIndex ? 'lightgray' : 'transparent',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onMouseDown={(e) => {
                    selectSuggestion(suggestion);
                    e.stopPropagation();
                  }}
                >
                  {suggestion}
                </li>
              ))}
            </ul>,
            document.body,
          )}
      </FilterBarInputBase>
    );
  },
);

interface FilterBarDatePickerProps
  extends Omit<FilterBarInputBaseProps, 'children'> {
  datePickerProps: DatePickerProps;
}

export const FilterBarDatePicker = ({
  datePickerProps,
  ...baseProps
}: FilterBarDatePickerProps): JSX.Element => {
  return (
    <FilterBarInputBase {...baseProps}>
      <Box>
        <DatePicker
          {...{
            ...datePickerProps,
            portalId: 'dropdown-root',
          }}
        />
      </Box>
    </FilterBarInputBase>
  );
};

interface FilterBarSelectProps
  extends Omit<FilterBarInputBaseProps, 'children'> {
  selectProps: ReactSelectProps;
}

export const FilterBarSelect = ({
  selectProps,
  ...baseProps
}: FilterBarSelectProps): JSX.Element => {
  return (
    <FilterBarInputBase {...baseProps}>
      <Box width={150}>
        <ReactSelect styles={reactSelectStyles} {...selectProps} />
      </Box>
    </FilterBarInputBase>
  );
};

interface FilterBarLocationSelectProps
  extends Omit<FilterBarInputBaseProps, 'children'> {
  selectProps: LocationSelectProps<LocationSelectValue>;
}

export const FilterBarLocationSelect = ({
  selectProps,
  ...baseProps
}: FilterBarLocationSelectProps): JSX.Element => {
  return (
    <FilterBarInputBase {...baseProps}>
      <Box width={150}>
        <LocationSelect {...selectProps} />
      </Box>
    </FilterBarInputBase>
  );
};

export const FilterBarSeparator = (props: ViewProps): JSX.Element => (
  <View width="1px" height="40px" bg="neutral.gray_400" mx={3} {...props} />
);
interface FilterBarDateRangePickerProps
  extends Omit<FilterBarInputBaseProps, 'children'> {
  datePickerProps: DateRangePickerProps;
}

export const FilterBarDateRangePicker = ({
  datePickerProps,
  ...baseProps
}: FilterBarDateRangePickerProps): JSX.Element => {
  return (
    <FilterBarInputBase overflow="visible" {...baseProps}>
      <Box>
        <DateRangePicker {...datePickerProps} />
      </Box>
    </FilterBarInputBase>
  );
};

export default FilterBar;
